import * as React from 'react';
import {
    ChangeEvent,
    FormEvent,
    ReactElement,
    ReactNode,
    useEffect,
    useState,
} from 'react';

import { del, patch } from '../../api/utils';

import Icon from './Icon';

interface IProps<T> {
    canEdit?: boolean;
    canDelete?: boolean;
    children?: ReactNode;
    csrfToken: string;
    max?: string | number;
    min?: string | number;
    name: string;
    onError?: (error: string | null) => void;
    onUpdate?: (response: T) => void;
    onDelete?: (response: T) => void;
    requestBodyContainer?: string;
    step?: string | number;
    type?: string;
    updateURI: string;
    deleteURI?: string;
    value: string | number;
}

export default function UniqueFieldForm<T>({
    canEdit = true,
    canDelete = false,
    children,
    csrfToken,
    max,
    min,
    name,
    onError,
    onUpdate,
    onDelete,
    requestBodyContainer,
    step,
    type = 'text',
    updateURI,
    deleteURI,
    value: initialValue,
}: IProps<T>): ReactElement {
    const [editing, setEditing] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
    const [value, setValue] = useState<string | number>(initialValue);
    const [inputValue, setInputValue] = useState<string | number>(initialValue);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    const onCancel = (): void => {
        setInputValue(initialValue);
        setEditing(false);
    };

    const onDeleteClicked = async (name: string): Promise<void> => {
        setIsDeleteLoading(true);

        try {
            const response = await del<T>(
                `${deleteURI}?name=${encodeURIComponent(name)}`,
                csrfToken,
            );
            onDelete?.(response);
        } catch (err) {
            onError?.(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const onChangeName = (e: ChangeEvent<HTMLInputElement>): void => {
        setInputValue(e.target.value);
    };

    const onSubmit = async (e?: FormEvent): Promise<void> => {
        e.preventDefault();

        if (inputValue === initialValue) {
            setEditing(false);
            return;
        }

        onError?.(null);
        setIsLoading(true);

        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let body: any = { [name]: inputValue };
            if (requestBodyContainer) {
                body = { [requestBodyContainer]: body };
            }

            const response = await patch<T>(updateURI, csrfToken, body);
            onUpdate?.(response);
            setValue(response[name]);
            setEditing(false);
        } catch (err) {
            onError?.(err.message);
            alert('Attendance name should be unique.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {editing ? (
                <form
                    className="align-items-center d-inline-flex gap-3"
                    onSubmit={onSubmit}
                >
                    <input
                        autoFocus={true}
                        className="form-control"
                        disabled={isLoading}
                        max={max}
                        min={min}
                        onChange={onChangeName}
                        step={step}
                        style={{
                            minWidth: '16ch',
                            width: `${inputValue.toString().length}ch`,
                        }}
                        type={type}
                        value={inputValue}
                    />

                    <small className="d-inline-flex gap-2">
                        {isLoading ? (
                            <Icon name="circle-o-notch" spin />
                        ) : (
                            <>
                                <Icon name="check" onClick={onSubmit} />
                                <Icon
                                    className="text-danger"
                                    name="times"
                                    onClick={onCancel}
                                />
                            </>
                        )}
                    </small>
                </form>
            ) : (
                <>
                    {children || value}
                    {canEdit && !isDeleteLoading && (
                        <small>
                            <Icon
                                className="ml-2"
                                name="pencil"
                                onClick={(): void => setEditing(true)}
                                title="Edit"
                            />
                        </small>
                    )}
                    {canDelete && !isDeleteLoading && (
                        <small>
                            <Icon
                                className="ml-2"
                                name="trash-o"
                                onClick={(): Promise<void> =>
                                    onDeleteClicked(name)
                                }
                                title="Delete"
                            />
                        </small>
                    )}
                    {isDeleteLoading && (
                        <small>
                            <Icon
                                className="ml-2"
                                name="spinner"
                                spin
                                title="Deleting..."
                            />
                        </small>
                    )}
                </>
            )}
        </>
    );
}
